'use client';

import { ActionIcon, Badge, Button, Modal, Text, Title } from 'rizzui';
import {
  PiChatTextBold,
  PiGavelBold,
  PiMapPinBold,
  PiPackageBold,
  PiTruckBold,
  PiXBold,
} from 'react-icons/pi';
import type {
  BidStatusOption,
  CustomerLoadboardItem,
  PublicShipment,
} from '@/server/api';
import { resolveStatusLabel } from '@/server/api';

function formatMoney(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString()} ${currency}`;
  }
}

function formatDate(value?: string | number | null) {
  if (!value) return '—';
  const date =
    typeof value === 'number'
      ? new Date(value < 10_000_000_000 ? value * 1000 : value)
      : new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function formatLocation(location?: {
  city?: string | null;
  state?: string | null;
}) {
  if (!location) return '—';
  return [location.city, location.state].filter(Boolean).join(', ') || '—';
}

function statusClass(label?: string) {
  const text = (label || '').toLowerCase();
  if (
    text.includes('accept') ||
    text.includes('approve') ||
    text.includes('booked')
  )
    return '!bg-emerald-100 !text-emerald-800 dark:!bg-emerald-500/15 dark:!text-emerald-300';
  if (
    text.includes('reject') ||
    text.includes('decline') ||
    text.includes('cancel')
  )
    return '!bg-red-100 !text-red-700 dark:!bg-red-500/15 dark:!text-red-300';
  if (text.includes('withdraw') || text === '—' || !text)
    return '!bg-slate-100 !text-slate-700 dark:!bg-slate-500/15 dark:!text-slate-300';
  return '!bg-amber-100 !text-amber-800 dark:!bg-amber-500/15 dark:!text-amber-300';
}

type CustomerLoadboardDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  statusList?: BidStatusOption[];
} & (
  | { item: CustomerLoadboardItem | null; load?: never }
  | { load: PublicShipment | null; item?: never }
);

export function CustomerLoadboardDetailsModal({
  item,
  load,
  isOpen,
  onClose,
  statusList = [],
}: CustomerLoadboardDetailsModalProps) {
  const shipment = item?.shipment ?? load;
  if (!shipment) return null;

  const isBook =
    item?.type?.value === 2 || item?.type?.label?.toLowerCase() === 'book';
  const displayStatus = item
    ? resolveStatusLabel(item.status, statusList)
    : shipment.status;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      customSize="720px"
      rounded="xl"
      overlayClassName="bg-[#06162b]/55 backdrop-blur-[3px]"
      containerClassName="!w-[calc(100vw-2rem)] overflow-hidden border border-white/10 bg-white p-0 shadow-[0_32px_90px_rgba(4,20,40,0.32)] sm:!w-[calc(100vw-3rem)] lg:!w-[720px] dark:bg-slate-900"
      className="z-[10020]"
    >
      <header className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-50 px-6 py-5 dark:border-slate-800 dark:bg-slate-800/60">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#081e37] text-[#ffd166] shadow">
            {!item || isBook ? (
              <PiTruckBold className="h-5 w-5 text-white" />
            ) : (
              <PiGavelBold className="h-5 w-5" />
            )}
          </span>
          <div>
            <div className="flex items-center gap-2">
              <Title
                as="h3"
                className="text-lg font-bold text-slate-900 dark:text-white"
              >
                {!item
                  ? 'Load Details'
                  : isBook
                    ? 'Booking Details'
                    : 'Bid Details'}{' '}
                — Load #{shipment.pro}
              </Title>
              <Badge className={statusClass(displayStatus)}>
                {displayStatus}
              </Badge>
            </div>
            {item ? (
              <Text className="text-xs text-slate-500 dark:text-slate-400">
                Submitted on {formatDate(item.created_at)}
              </Text>
            ) : null}
          </div>
        </div>
        <ActionIcon
          variant="text"
          onClick={onClose}
          aria-label="Close load details"
          className="!text-slate-400 hover:!text-slate-700 dark:hover:!text-white"
        >
          <PiXBold className="h-5 w-5" />
        </ActionIcon>
      </header>

      <div className="max-h-[calc(85vh-130px)] space-y-5 overflow-y-auto px-6 py-6">
        {/* Summary Rate & Status Cards */}
        <div
          className={`grid grid-cols-1 gap-3 ${item ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}
        >
          {item ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
              <Text className="text-xs font-medium text-slate-500 dark:text-slate-400">
                {isBook ? 'Agreed Rate' : 'Your Bid Amount'}
              </Text>
              <Text className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                {formatMoney(item.amount, item.currency || 'USD')}
              </Text>
            </div>
          ) : null}
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
            <Text className="text-xs font-medium text-slate-500 dark:text-slate-400">
              Status
            </Text>
            <div className="mt-1">
              <Badge className={statusClass(displayStatus)}>
                {displayStatus}
              </Badge>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
            <Text className="text-xs font-medium text-slate-500 dark:text-slate-400">
              Listed Load Rate
            </Text>
            <Text className="mt-1 text-xl font-semibold text-slate-700 dark:text-slate-200">
              {shipment.price
                ? formatMoney(shipment.price, 'USD')
                : 'Call for quote'}
            </Text>
          </div>
        </div>

        {/* Message / Note */}
        {item?.message && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-800/40">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              <PiChatTextBold className="h-4 w-4" />
              <span>Note / Message</span>
            </div>
            <Text className="mt-2 whitespace-pre-wrap text-sm text-slate-800 dark:text-slate-200">
              {item.message}
            </Text>
          </div>
        )}

        {/* Route & Schedule */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-800/40">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <PiMapPinBold className="h-4 w-4" />
            <span>Route & Schedule</span>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-800/50">
              <Text className="text-xs font-medium text-slate-500">
                Origin (Pickup)
              </Text>
              <Text className="mt-1 font-semibold text-slate-900 dark:text-white">
                {formatLocation(shipment.pickup)}
              </Text>
              <Text className="mt-1 text-xs text-slate-500">
                Estimated pickup:{' '}
                {formatDate(shipment.estimated_times?.pickup_at)}
              </Text>
            </div>
            <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-800/50">
              <Text className="text-xs font-medium text-slate-500">
                Destination (Delivery)
              </Text>
              <Text className="mt-1 font-semibold text-slate-900 dark:text-white">
                {formatLocation(shipment.destination)}
              </Text>
              <Text className="mt-1 text-xs text-slate-500">
                Estimated delivery:{' '}
                {formatDate(shipment.estimated_times?.delivery_at)}
              </Text>
            </div>
          </div>
          {shipment.miles && (
            <div className="mt-3 text-xs text-slate-500">
              Distance:{' '}
              <span className="font-medium text-slate-700 dark:text-slate-300">
                {shipment.miles} miles
              </span>
            </div>
          )}
        </div>

        {/* Freight & Equipment Details */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-800/40">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <PiPackageBold className="h-4 w-4" />
            <span>Freight & Equipment</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div>
              <Text className="text-xs text-slate-500">Equipment</Text>
              <Text className="mt-0.5 font-medium text-slate-900 dark:text-white">
                {shipment.freight?.equipment_type?.description ||
                  shipment.freight?.equipment_type?.code ||
                  '—'}
              </Text>
            </div>
            <div>
              <Text className="text-xs text-slate-500">Weight</Text>
              <Text className="mt-0.5 font-medium text-slate-900 dark:text-white">
                {shipment.freight?.weight
                  ? `${shipment.freight.weight.toLocaleString()} lbs`
                  : '—'}
              </Text>
            </div>
            <div>
              <Text className="text-xs text-slate-500">Pieces / Pallets</Text>
              <Text className="mt-0.5 font-medium text-slate-900 dark:text-white">
                {shipment.freight?.pieces
                  ? `${shipment.freight.pieces} pcs`
                  : shipment.freight?.pallets
                    ? `${shipment.freight.pallets} pallets`
                    : '—'}
              </Text>
            </div>
            <div>
              <Text className="text-xs text-slate-500">Temperature</Text>
              <Text className="mt-0.5 font-medium text-slate-900 dark:text-white">
                {shipment.freight?.temperature !== null &&
                shipment.freight?.temperature !== undefined
                  ? `${shipment.freight.temperature}°F`
                  : 'Standard'}
              </Text>
            </div>
          </div>
          {shipment.freight?.description && (
            <div className="mt-3 border-t border-slate-100 pt-3 dark:border-slate-700/50">
              <Text className="text-xs text-slate-500">Description</Text>
              <Text className="mt-0.5 text-sm text-slate-800 dark:text-slate-200">
                {shipment.freight.description}
              </Text>
            </div>
          )}
        </div>
      </div>

      <footer className="flex items-center justify-end border-t border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/60">
        <Button onClick={onClose} variant="outline">
          Close
        </Button>
      </footer>
    </Modal>
  );
}
